import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, Loader2, Eye, Clock, User, Zap, Gauge, Thermometer, Target, Wifi, Settings, Activity, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { ZoneViewFilters } from './ui/ZoneViewFilters';
import { useZoneView } from '../redux/WeldData/hooks/useWeldData';
import { formatTimeForDisplay, formatDuration } from '../lib/formatUtils';
import { convertWeldDataValue, getParameterUnitSuffix, getUnitFromSettings, formatTimestampForDisplay } from '../utils/unitUtils';
import { extractZoneAndPassForM500FromTableLimits, extractM500ZoneDetailsFromData } from '../utils/m500ZoneUtils';
import { getZoneStatus, getStatusColorClass } from '../utils/statusUtils';

// Add custom scrollbar styles
const scrollbarStyles = `
  .scrollbar-thin::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }

  /* For Firefox */
  .scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: #9ca3af #f1f1f1;
  }
`;

// Define parameter limits (in inches - will be converted based on display unit)
const PARAMETER_LIMITS = {
  voltage: { min: 20, max: 30 },
  current: { min: 150, max: 250 },
  travelSpeed: { min: 100, max: 300 },
  heat: { min: 1.0, max: 2.0 },
  trueEnergy: { min: 2.5, max: 4.5 },
  oscillationWidth: { min: 0.01, max: 10.0 },
  wireSpeed: { min: 150, max: 450 },
  totalWireConsumed: { min: 0, max: 1000 },
  distance: { min: 0, max: 1000 }
};

// Get converted parameter limits based on display unit
const getConvertedParameterLimits = () => {
  const displayUnit = getUnitFromSettings();
  
  if (displayUnit === 'Inches') {
    return PARAMETER_LIMITS;
  }
  
  // Convert limits from inches to mm for display
  return {
    voltage: PARAMETER_LIMITS.voltage,
    current: PARAMETER_LIMITS.current,
    travelSpeed: { 
      min: PARAMETER_LIMITS.travelSpeed.min * 25.4, 
      max: PARAMETER_LIMITS.travelSpeed.max * 25.4 
    },
    heat: PARAMETER_LIMITS.heat,
    trueEnergy: PARAMETER_LIMITS.trueEnergy,
    oscillationWidth: { 
      min: PARAMETER_LIMITS.oscillationWidth.min * 25.4, 
      max: PARAMETER_LIMITS.oscillationWidth.max * 25.4 
    },
    wireSpeed: { 
      min: PARAMETER_LIMITS.wireSpeed.min * 25.4, 
      max: PARAMETER_LIMITS.wireSpeed.max * 25.4 
    },
    totalWireConsumed: { 
      min: PARAMETER_LIMITS.totalWireConsumed.min * 25.4, 
      max: PARAMETER_LIMITS.totalWireConsumed.max * 25.4 
    },
    distance: { 
      min: PARAMETER_LIMITS.distance.min * 25.4, 
      max: PARAMETER_LIMITS.distance.max * 25.4 
    }
  };
};

const STATUS_CALCULATION_MIN_DELAY_MS = 20000;
const STATUS_CALCULATION_DELAY_PER_ROW_MS = 1000;

// Sorting types
interface Sorting {
  field: string;
  order: 'ascend' | 'descend';
}

interface ZoneViewTableProps {
  weld: any;
  onRowClick?: (row: ZoneViewRow) => void;
  statusCalculationMethod?: string;
  filters?: {
    stationNameFilter: string;
    statusFilter: string;
    bugTypeFilter: string;
    torchFilter: string;
    passFilter: string;
    zoneFilter: string;
    dateRange: { dateFrom: Date | undefined; dateTo: Date | undefined };
  };
  onFilterChange?: (filterType: string, value: any) => void;
  // M500 related props
  tableLimitRecords?: any[];
  isM500?: boolean;
  visibleColumns?: string[];
  // Customer representative and project type props
  isCustomerRepresentative?: boolean;
  isBuckleArrestorProject?: boolean;
  statusCalculationStartedAt?: number | null;
}

// Define the type for a row
interface ZoneViewRow {
  id: number;
  status: string;
  zone_status_by_instantaneuous?: string;
  stationName: string;
  bugType: string;
  torch: string;
  pass: string;
  zone: string;
  weldStartTime: string;
  weldTime: number | null;
  autoincrement: string;
  welduid_s_event_torch?: string;
  bugNumber?: string;
  bandDiameter?: number | null;
  wireDiameter?: number | null;
  loggingDistance?: number | null;
  pipeDiameter?: number | null;
  // M500 zone details (saga maps API job_number to jobNumber)
  job_number?: string;
  jobNumber?: string;
  zone_name?: string;
  m500_zone?: string;
  pipe_name?: string;
  pass_name?: string;
  welding_system?: string;
  m500ZoneDetails?: {
    PipeNum: string;
    PassNum: string;
    ZoneNum: string;
  };
  isM500System?: boolean;
  current: { min: number | null; avg: number | null; max: number | null };
  volt: { min: number | null; avg: number | null; max: number | null };
  travelSpeed: { min: number | null; avg: number | null; max: number | null };
  trueEnergy: { min: number | null; avg: number | null; max: number | null };
  heat: { min: number | null; avg: number | null; max: number | null };
  wireSpeed: { min: number | null; avg: number | null; max: number | null };
  frequency: { min: number | null; avg: number | null; max: number | null };
  target: { min: number | null; avg: number | null; max: number | null };
  horizontalBias: { min: number | null; avg: number | null; max: number | null };
  totalWireConsumed: { min: number | null; avg: number | null; max: number | null };
  arcImpedance: { min: number | null; avg: number | null; max: number | null };
  oscillationWidth: { min: number | null; avg: number | null; max: number | null };
  passView?: any;
}

// Helper function to check if a parameter is within limits
const isWithinLimits = (value: number, paramName: keyof typeof PARAMETER_LIMITS): boolean => {
  const limits = getConvertedParameterLimits()[paramName];
  return value >= limits.min && value <= limits.max;
};

// Helper function to render min/avg/max values with proper formatting
// This will be redefined inside the component to access visibleColumns

const ZoneViewTable: React.FC<ZoneViewTableProps> = React.memo(({ weld, onRowClick, statusCalculationMethod = 'Instantaneous', filters, onFilterChange, tableLimitRecords, isM500, visibleColumns, isCustomerRepresentative = false, isBuckleArrestorProject = false, statusCalculationStartedAt = null }) => {
  const { zoneViewData, zoneViewLoading, zoneViewError, loadZoneView } = useZoneView();
  const [isStatusCalculationPending, setIsStatusCalculationPending] = useState(false);

  // Helper function to check if a column is visible
  const isColumnVisible = useCallback((columnKey: string): boolean => {
    if (!visibleColumns || visibleColumns.length === 0) {
      return true; // Show all columns if no visibility specified
    }
    return visibleColumns.includes(columnKey);
  }, [visibleColumns]);

  // Helper function to check if a sub-column is visible
  const isSubColumnVisible = useCallback((columnKey: string, subType: 'min' | 'avg' | 'max'): boolean => {
    if (!visibleColumns || visibleColumns.length === 0) return true;
    const subKey = `${columnKey}-${subType}`;
    return visibleColumns.includes(subKey);
  }, [visibleColumns]);

  // Helper function to render min/avg/max values with proper formatting
  const renderMinAvgMax = useCallback((
    values?: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean }, 
    decimals: number = 2, 
    paramName?: keyof typeof PARAMETER_LIMITS,
    columnKey?: string
  ) => {
    if (!values || (values.min === null && values.avg === null && values.max === null)) {
      const visibleSubs: ('min' | 'avg' | 'max')[] = [];
      if (!columnKey || isSubColumnVisible(columnKey, 'min')) visibleSubs.push('min');
      if (!columnKey || isSubColumnVisible(columnKey, 'avg')) visibleSubs.push('avg');
      if (!columnKey || isSubColumnVisible(columnKey, 'max')) visibleSubs.push('max');
      
      if (visibleSubs.length === 0) return null;
      
      return (
        <div className="grid gap-1 h-full" style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}>
          {visibleSubs.map((_, idx) => (
            <div key={idx} className="text-gray-400 flex items-center justify-center h-full">-</div>
          ))}
        </div>
      );
    }

    const renderValue = (value: number | null, isAvg: boolean = false, status?: boolean) => {
      if (value === null) return '-';
      
      // Convert value from inches to display unit if needed
      let displayValue;
      if (paramName && ['travelSpeed', 'wireSpeed', 'heat', 'trueEnergy', 'oscillationWidth', 'totalWireConsumed', 'distance'].includes(paramName)) {
        // Convert value from inches to display unit for distance-based parameters
        const convertedValue = convertWeldDataValue(value, paramName);
        displayValue = typeof convertedValue === 'number' ? convertedValue.toFixed(decimals) : '-';
      } else {
        // Keep original value for voltage, current, impedance, etc.
        displayValue = value.toFixed(decimals);
      }
      
      // Apply red background if status is false, otherwise keep normal styling
      const bgColor = status === false ? 'bg-red-100' : 'bg-white';
      const textColor = status === false ? 'text-red-800' : (isAvg ? 'text-gray-900' : 'text-blue-600');
      
      return (
        <div className={`flex items-center justify-center h-full w-full ${bgColor} ${textColor} rounded-lg px-1 font-medium text-xs break-all`}>
          {displayValue}
        </div>
      );
    };

    // Determine which sub-columns are visible
    const visibleSubs: Array<{type: 'min' | 'avg' | 'max', value: number | null, status?: boolean}> = [];
    if (!columnKey || isSubColumnVisible(columnKey, 'min')) {
      visibleSubs.push({ type: 'min', value: values.min, status: values.lowStatus });
    }
    if (!columnKey || isSubColumnVisible(columnKey, 'avg')) {
      visibleSubs.push({ type: 'avg', value: values.avg, status: values.avgStatus });
    }
    if (!columnKey || isSubColumnVisible(columnKey, 'max')) {
      visibleSubs.push({ type: 'max', value: values.max, status: values.highStatus });
    }

    if (visibleSubs.length === 0) return null;

    return (
      <div className="grid gap-1 bg-gray-50 rounded-md h-full min-w-0" style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}>
        {visibleSubs.map((sub) => (
          <div key={sub.type} className="flex items-center justify-center h-full min-w-0">
            <div className="flex items-center justify-center h-full w-full min-w-0">
              {renderValue(sub.value, sub.type === 'avg', sub.status)}
            </div>
          </div>
        ))}
      </div>
    );
  }, [isSubColumnVisible]);

  // Helper function to render header labels based on visible sub-columns
  const renderHeaderLabels = useCallback((columnKey: string) => {
    const visibleSubs: ('min' | 'avg' | 'max')[] = [];
    if (isSubColumnVisible(columnKey, 'min')) visibleSubs.push('min');
    if (isSubColumnVisible(columnKey, 'avg')) visibleSubs.push('avg');
    if (isSubColumnVisible(columnKey, 'max')) visibleSubs.push('max');
    
    if (visibleSubs.length === 0) return null;
    
    const labels: Record<'min' | 'avg' | 'max', string> = { min: 'Min', avg: 'Avg', max: 'Max' };
    
    return (
      <div className="grid text-xs font-normal text-center text-gray-500" style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}>
        {visibleSubs.map(sub => (
          <div key={sub}>{labels[sub]}</div>
        ))}
      </div>
    );
  }, [isSubColumnVisible]);

  // Format job/wps numbers: if more than 2 values, show all comma-separated
  const formatJobNumbers = useCallback((value: string | string[] | null | undefined): string => {
    if (value == null || value === '') return '-';
    const parts = Array.isArray(value)
      ? value.filter((v) => v != null && String(v).trim() !== '')
      : String(value)
          .split(/[,;|]/)
          .map((s) => s.trim())
          .filter(Boolean);
    if (parts.length === 0) return '-';
    return parts.join(', ');
  }, []);

  // Show job number column only when weld has multiple job numbers (comma/semicolon-separated)
  const jobNumberColumnVisible = useMemo(() => {
    const raw = weld?.wpsNumber;
    if (!raw || typeof raw !== 'string') return false;
    const parts = raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    return parts.length > 1;
  }, [weld?.wpsNumber]);

  const isJobNumberColumnShown = isColumnVisible('job-number') && jobNumberColumnVisible;

  // Helper function to calculate sticky left position for a column
  const getStickyLeft = useCallback((columnKey: string): string => {
    const stickyColumnOrder = ['sl-no', 'status', 'job-number', 'station', 'actions'];
    const stickyColumnWidths: Record<string, number> = {
      'sl-no': 80,
      'status': 60,
      'job-number': 120,
      'station': 140,
      'actions': 100
    };
    const isStickyColVisible = (key: string) =>
      key === 'job-number' ? isJobNumberColumnShown : isColumnVisible(key);
    let left = 0;
    const currentIndex = stickyColumnOrder.indexOf(columnKey);
    for (let i = 0; i < currentIndex; i++) {
      if (isStickyColVisible(stickyColumnOrder[i])) {
        left += stickyColumnWidths[stickyColumnOrder[i]];
      }
    }
    return `${left}px`;
  }, [isColumnVisible, isJobNumberColumnShown]);

  // Local filter states for ZoneView specific filters
  const [stationNameFilter, setStationNameFilter] = useState('');
  const [bugTypeFilter, setBugTypeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [torchFilter, setTorchFilter] = useState<string[]>([]);
  const [passFilter, setPassFilter] = useState<string[]>([]);
  const [zoneFilter, setZoneFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ dateFrom: Date | undefined; dateTo: Date | undefined }>({
    dateFrom: undefined,
    dateTo: undefined
  });

  // Sorting state
  const [sorting, setSorting] = useState<Sorting | null>(null);

  // Process data with M500 zone details - moved before dynamicHeaders
  const processedData = useMemo(() => {
    return zoneViewData.map((row: ZoneViewRow) => {
      // Check if it's an M500 system
      const isM500System = row?.welding_system?.toLowerCase() === "m500";
      
      
      // Extract M500 zone details if it's an M500 system
      let m500ZoneData = {};
      if (isM500System) {
        // Use the function designed for direct extraction from the row object
        m500ZoneData = extractM500ZoneDetailsFromData(row);
      } else if (isM500 && tableLimitRecords && row.job_number && row.zone_name) {
        // Fallback to table limits lookup if isM500 prop is true and data is available
        m500ZoneData = extractZoneAndPassForM500FromTableLimits(
          tableLimitRecords || [],
          row.job_number,
          row.zone_name
        );
      }

      return {
        ...row,
        m500ZoneDetails: m500ZoneData,
        isM500System
      };
    });
  }, [zoneViewData, isM500, tableLimitRecords]);

  // Check if we have any M500 systems in the data - moved before dynamicHeaders
  const hasM500Data = useMemo(() => {
    return processedData.some((row: ZoneViewRow) => row.isM500System === true);
  }, [processedData]);



  // Get dynamic unit labels for table headers
  const getUnitLabel = (parameter: string): string => {
    return getParameterUnitSuffix(parameter);
  };

  // Create dynamic headers with unit labels - filter out M500-specific columns if no M500 data
  // Map internal keys to column keys for sub-column visibility
  const keyMapping: Record<string, string> = {
    'current': 'current',
    'volt': 'voltage',
    'travelSpeed': 'travel-speed',
    'trueEnergy': 'true-energy',
    'heat': 'heat',
    'wireSpeed': 'wire-speed',
    'oscillationWidth': 'oscillation-width',
    'frequency': 'frequency',
    'target': 'target',
    'horizontalBias': 'horizontal-bias',
    'totalWireConsumed': 'total-wire-consumed',
    'arcImpedance': 'arc-impedance'
  };

  const allDynamicHeaders = [
    { key: 'current', label: `Current (${getUnitLabel('current')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.current, 0, 'current', keyMapping['current']) },
    { key: 'volt', label: `Voltage (${getUnitLabel('volts')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.volt, 2, 'voltage', keyMapping['volt']) },
    { key: 'travelSpeed', label: `Travel Speed (${getUnitLabel('travel_speed')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.travelSpeed, 1, 'travelSpeed', keyMapping['travelSpeed']) },
    { key: 'trueEnergy', label: `True Energy (${getUnitLabel('true_energy')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.trueEnergy, 2, 'trueEnergy', keyMapping['trueEnergy']) },
    { key: 'heat', label: `Heat (${getUnitLabel('heat')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.heat, 2, 'heat', keyMapping['heat']) },
    { key: 'wireSpeed', label: `Wire Speed (${getUnitLabel('wire_speed')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.wireSpeed, 0, 'wireSpeed', keyMapping['wireSpeed']) },
    { key: 'oscillationWidth', label: `Oscillation Width (${getUnitLabel('oscillation_width')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.oscillationWidth, 2, 'oscillationWidth', keyMapping['oscillationWidth']) },
    { key: 'frequency', label: `Frequency (${getUnitLabel('frequency')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.frequency, 0, undefined, keyMapping['frequency']) },
    { key: 'target', label: `Target (${getUnitLabel('target')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.target, 0, undefined, keyMapping['target']) },
    { key: 'horizontalBias', label: `Horizontal Bias (${getUnitLabel('horizontal_bias')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.horizontalBias, 0, undefined, keyMapping['horizontalBias']) },
    { key: 'totalWireConsumed', label: `Total Wire Consumed (${getUnitLabel('total_wire_consumed')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.totalWireConsumed, 0, 'totalWireConsumed', keyMapping['totalWireConsumed']) },
    { key: 'arcImpedance', label: `Arc Impedance (${getUnitLabel('arc_impedance')})`, render: (row: ZoneViewRow) => renderMinAvgMax(row.arcImpedance, 0, undefined, keyMapping['arcImpedance']) },
  ];

  // Filter out Arc Impedance column if no M500 data, and filter by visibleColumns
  const dynamicHeaders = useMemo(() => {
    let filtered = hasM500Data ? allDynamicHeaders : allDynamicHeaders.filter(header => header.key !== 'arcImpedance');
    
    // Filter by visibleColumns if provided
    if (visibleColumns && visibleColumns.length > 0) {
      // Reverse mapping: from internal key to column key
      const reverseKeyMapping: Record<string, string> = {
        'current': 'current',
        'volt': 'voltage',
        'travelSpeed': 'travel-speed',
        'trueEnergy': 'true-energy',
        'heat': 'heat',
        'wireSpeed': 'wire-speed',
        'oscillationWidth': 'oscillation-width',
        'frequency': 'frequency',
        'target': 'target',
        'horizontalBias': 'horizontal-bias',
        'totalWireConsumed': 'total-wire-consumed',
        'arcImpedance': 'arc-impedance'
      };
      
      filtered = filtered.filter(header => {
        // Find the visibleColumns key that matches this header
        const visibleKey = reverseKeyMapping[header.key];
        return visibleKey ? visibleColumns.includes(visibleKey) : true;
      });
    }
    
    return filtered;
  }, [hasM500Data, visibleColumns]);

const minAvgMaxKeys = [
  'current',
  'volt',
  'travelSpeed',
  'trueEnergy',
  'heat',
  'wireSpeed',
  'oscillationWidth',
  'frequency',
  'target',
  'horizontalBias',
  'totalWireConsumed',
  'arcImpedance',
];

  // Sorting function
  const handleSort = (field: string) => {
    if (sorting?.field === field) {
      setSorting(sorting.order === 'ascend' ? { field, order: 'descend' } : null);
    } else {
      setSorting({ field, order: 'ascend' });
    }
  };

  // Render sort icon
  const renderSortIcon = (field: string) => {
    if (sorting?.field !== field) {
      return <ChevronUp className="h-3 w-3 text-gray-400" />;
    }
    return sorting.order === 'ascend' ? 
      <ChevronUp className="h-3 w-3 text-blue-600" /> : 
      <ChevronDown className="h-3 w-3 text-blue-600" />;
  };

  // Extract unique options for dropdowns
  const uniqueOptions = useMemo(() => {
    const bugTypes = Array.from(new Set(zoneViewData.map((row: ZoneViewRow) => row.bugType).filter((type: string | undefined): type is string => Boolean(type)))) as string[];
    const statuses = Array.from(new Set(zoneViewData.map((row: ZoneViewRow) => row.status))) as string[];
    const torches = Array.from(new Set(zoneViewData.map((row: ZoneViewRow) => row.torch).filter((torch: string | undefined): torch is string => Boolean(torch)))) as string[];
    const passes = Array.from(new Set(zoneViewData.map((row: ZoneViewRow) => row.pass).filter((pass: string | undefined): pass is string => Boolean(pass)))) as string[];
    const zones = Array.from(new Set(zoneViewData.map((row: ZoneViewRow) => row.zone).filter((zone: string | undefined): zone is string => Boolean(zone)))) as string[];
    
    return {
      bugTypes,
      statuses,
      torches,
      passes,
      zones
    };
  }, [zoneViewData]);

  // Apply filters to data
  const filteredData = useMemo(() => {
    let filtered = processedData.filter((row: ZoneViewRow) => {
      // Station name filter
      if (stationNameFilter && row.stationName) {
        const stationMatch = row.stationName.toLowerCase().includes(stationNameFilter.toLowerCase());
        if (!stationMatch) return false;
      }

      // Bug type filter
      if (bugTypeFilter.length > 0 && row.bugType && !bugTypeFilter.includes(row.bugType)) {
        return false;
      }

      // Status filter
      if (statusFilter.length > 0) {
        const status = getZoneStatus(statusCalculationMethod, row);
        if (status && !statusFilter.includes(status)) {
          return false;
        }
      }

      // Torch filter
      if (torchFilter.length > 0 && row.torch && !torchFilter.includes(row.torch)) {
        return false;
      }

      // Pass filter
      if (passFilter.length > 0 && row.pass && !passFilter.includes(row.pass)) {
        return false;
      }

      // Zone filter
      if (zoneFilter.length > 0 && row.zone && !zoneFilter.includes(row.zone)) {
        return false;
      }

      // Date range filter
      if (dateRange.dateFrom || dateRange.dateTo) {
        const rowStartDate = row.weldStartTime ? new Date(row.weldStartTime) : null;
        
        if (rowStartDate) {
          const startDate = new Date(rowStartDate);
          startDate.setHours(0, 0, 0, 0);
          
          if (dateRange.dateFrom) {
            const fromDate = new Date(dateRange.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            if (startDate < fromDate) {
              return false;
            }
          }
          if (dateRange.dateTo) {
            const toDate = new Date(dateRange.dateTo);
            toDate.setHours(23, 59, 59, 999);
            if (startDate > toDate) {
              return false;
            }
          }
        }
      }

      return true;
    });

    // Apply sorting
    if (sorting) {
      filtered.sort((a: ZoneViewRow, b: ZoneViewRow) => {
        let aValue: any;
        let bValue: any;

        switch (sorting.field) {
          case 'stationName':
            aValue = a.stationName || '';
            bValue = b.stationName || '';
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          case 'bugType':
            aValue = a.bugType || '';
            bValue = b.bugType || '';
            break;
          case 'torch':
            aValue = a.torch || '';
            bValue = b.torch || '';
            break;
          case 'pass':
            aValue = a.pass || '';
            bValue = b.pass || '';
            break;
          case 'zone':
            aValue = a.zone || '';
            bValue = b.zone || '';
            break;
          case 'weldTime':
            aValue = a.weldTime || 0;
            bValue = b.weldTime || 0;
            break;
          case 'weldStartTime':
            aValue = a.weldStartTime || '';
            bValue = b.weldStartTime || '';
            break;
          case 'welderId':
            aValue = a.passView?.welderId || '';
            bValue = b.passView?.welderId || '';
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sorting.order === 'ascend' ? -1 : 1;
        if (aValue > bValue) return sorting.order === 'ascend' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [processedData, stationNameFilter, bugTypeFilter, statusFilter, torchFilter, passFilter, zoneFilter, dateRange, sorting]);

  useEffect(() => {
    if (!statusCalculationStartedAt) {
      setIsStatusCalculationPending(false);
      return;
    }

    const rowCount = Math.max(zoneViewData.length, 1);
    const calculationDelay = Math.max(
      STATUS_CALCULATION_MIN_DELAY_MS,
      rowCount * STATUS_CALCULATION_DELAY_PER_ROW_MS,
    );
    const remainingDelay = calculationDelay - (Date.now() - statusCalculationStartedAt);

    if (remainingDelay <= 0) {
      setIsStatusCalculationPending(false);
      return;
    }

    setIsStatusCalculationPending(true);

    const timer = setTimeout(() => {
      if (weld?.weldNumber) {
        loadZoneView(weld.weldNumber);
      }
      setIsStatusCalculationPending(false);
    }, remainingDelay);

    return () => clearTimeout(timer);
  }, [statusCalculationStartedAt, zoneViewData.length, weld?.weldNumber, loadZoneView]);

  const renderZoneStatusIcon = (row: ZoneViewRow) => {
    if (isStatusCalculationPending) {
      return <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />;
    }

    const status = getZoneStatus(statusCalculationMethod, row);
    if (status === 'TRUE') {
      return <ThumbsUp className="h-5 w-5 text-green-600" />;
    }
    if (status === 'FALSE') {
      return <ThumbsDown className="h-5 w-5 text-red-600" />;
    }
    if (status === 'EX') {
      return <span className="h-5 w-5 flex items-center justify-center font-bold text-sm bg-yellow-400 text-black rounded-full">!</span>;
    }
    if (status === 'CROSS') {
      return <span className="h-5 w-5 flex items-center justify-center font-bold text-sm text-red-600">×</span>;
    }
    if (status === 'NULL' || status === null || status === undefined) {
      return <span className="text-gray-600">-</span>;
    }
    return <div className="h-5 w-5 rounded-full bg-yellow-400 animate-pulse" />;
  };

  return (
    <div className="h-full flex flex-col w-full" >
      <style>{scrollbarStyles}</style>

      {/* Filters Section */}
      <ZoneViewFilters
        stationNameFilter={stationNameFilter}
        onStationNameFilterChange={setStationNameFilter}
        bugTypeFilter={bugTypeFilter}
        onBugTypeFilterChange={setBugTypeFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        torchFilter={torchFilter}
        onTorchFilterChange={setTorchFilter}
        passFilter={passFilter}
        onPassFilterChange={setPassFilter}
        zoneFilter={zoneFilter}
        onZoneFilterChange={setZoneFilter}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        bugTypeOptions={uniqueOptions.bugTypes}
        statusOptions={uniqueOptions.statuses}
        torchOptions={uniqueOptions.torches}
        passOptions={uniqueOptions.passes}
        zoneOptions={uniqueOptions.zones}
        totalCount={zoneViewData.length}
        filteredCount={filteredData.length}
      />

      {/* Table Section */}
      <div className="flex-1 relative">
        {zoneViewLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
              <span className="text-gray-600 text-lg">Loading zone view data...</span>
            </div>
          </div>
        ) : zoneViewError ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-red-600 text-center">
              <p>Error loading zone view data:</p>
              <p className="text-sm">{zoneViewError}</p>
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500 text-center">
              <p>No zone view data available for this weld.</p>
              {stationNameFilter || bugTypeFilter.length > 0 || statusFilter.length > 0 || torchFilter.length > 0 || passFilter.length > 0 || zoneFilter.length > 0 || dateRange.dateFrom || dateRange.dateTo ? (
                <p className="text-sm mt-2">Try adjusting your filters.</p>
              ) : null}
            </div>
          </div>
        ) : (
          <div 
            className="absolute inset-0 overflow-x-auto overflow-y-auto pb-20 scrollbar-thin"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              overscrollBehaviorX: 'contain'
            }}
          >
            {/* Single unified table with sticky columns */}
            <table className="border-collapse w-full" style={{ tableLayout: 'auto', minWidth: 'max-content' }}>
              <thead className="sticky top-0 bg-white z-30">
                    <tr className="border-b h-[72px] bg-gray-50">
                  {/* Sticky columns */}
                  {isColumnVisible('sl-no') && (
                    <th className="sticky z-20 bg-gray-50 w-[80px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('sl-no') }}>
                      <div className="flex items-center justify-center gap-2">
                        <Target className="h-4 w-4" />
                        Sl.no
                      </div>
                    </th>
                  )}
                  {isColumnVisible('status') && (
                    <th className="sticky z-20 bg-gray-50 min-w-[60px] w-[60px] max-w-[60px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ width: '60px', left: getStickyLeft('status') }}>
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Activity className="h-4 w-4" />
                        Status
                        {renderSortIcon('status')}
                      </button>
                    </th>
                  )}
                  {isJobNumberColumnShown && (
                    <th className="sticky z-20 bg-gray-50 w-[120px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('job-number') }}>
                      Job Number
                    </th>
                  )}
                  {isColumnVisible('station') && (
                    <th className="sticky z-20 bg-gray-50 w-[140px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('station') }}>
                      <button
                        onClick={() => handleSort('stationName')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Target className="h-4 w-4" />
                        Station
                        {renderSortIcon('stationName')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('actions') && !(isCustomerRepresentative && isBuckleArrestorProject) && (
                    <th className="sticky z-20 bg-gray-50 w-[100px] px-4 text-center text-xs font-medium text-gray-600" style={{ left: getStickyLeft('actions'), boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>
                      <div className="flex items-center justify-center gap-2">
                        <Settings className="h-4 w-4" />
                        Actions
                      </div>
                    </th>
                  )}
                  
                  {/* Scrollable columns */}
                  { isColumnVisible('welder-id') && (
                    <th className="w-[140px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('welderId')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <User className="h-4 w-4" />
                        Welder ID
                        {renderSortIcon('welderId')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('bug-type') && (
                    <th className="w-[140px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('bugType')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Settings className="h-4 w-4" />
                        Bug Type
                        {renderSortIcon('bugType')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('torch') && (
                    <th className="w-[140px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('torch')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <User className="h-4 w-4" />
                        Torch
                        {renderSortIcon('torch')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('pass') && (
                    <th className="w-[140px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('pass')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Target className="h-4 w-4" />
                        Pass
                        {renderSortIcon('pass')}
                      </button>
                    </th>
                  )}
                  {hasM500Data && isColumnVisible('pipe') && (
                    <th className="w-[140px] px-4 text-center text-xs font-medium text-gray-600">
                      <div className="flex items-center justify-center gap-2">
                        <Target className="h-4 w-4" />
                        Pipe
                      </div>
                    </th>
                  )}
                  {isColumnVisible('zone') && (
                    <th className="w-[140px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('zone')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Target className="h-4 w-4" />
                        Zone
                        {renderSortIcon('zone')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('weld-start-time') && (
                    <th className="w-[160px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('weldStartTime')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Clock className="h-4 w-4" />
                        Weld Start Time
                        {renderSortIcon('weldStartTime')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('weld-time') && (
                    <th className="w-[80px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('weldTime')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Gauge className="h-4 w-4" />
                        Weld Time
                        {renderSortIcon('weldTime')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('auto-increment') && (
                    <th className="w-[160px] px-4 text-center text-xs font-medium text-gray-600 border-r">
                      <button
                        onClick={() => handleSort('autoincrement')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Target className="h-4 w-4" />
                        Auto Increment
                        {renderSortIcon('autoincrement')}
                      </button>
                    </th>
                  )}
                  
                  {/* Parameter columns */}
                      {dynamicHeaders.map((header, index) => (
                        <th key={header.key} className={`px-2 min-w-[150px] max-w-[250px] w-auto ${index > 0 ? 'border-l border-gray-200' : ''}`}>
                          <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                            <div className="flex items-center gap-1">
                              {header.key === 'weldStartTime' && <Clock className="h-4 w-4" />}
                              {header.key === 'weldTime' && <Gauge className="h-4 w-4" />}
                              {header.key === 'autoincrement' && <Target className="h-4 w-4" />}
                              {header.key === 'volt' && <Zap className="h-4 w-4" />}
                              {header.key === 'current' && <Zap className="h-4 w-4" />}
                              {header.key === 'travelSpeed' && <Gauge className="h-4 w-4" />}
                              {header.key === 'wireSpeed' && <Gauge className="h-4 w-4" />}
                              {header.key === 'heat' && <Thermometer className="h-4 w-4" />}
                              {header.key === 'trueEnergy' && <Activity className="h-4 w-4" />}
                              {header.key === 'oscillationWidth' && <Wifi className="h-4 w-4" />}
                              {header.key === 'target' && <Target className="h-4 w-4" />}
                              {header.key === 'horizontalBias' && <Settings className="h-4 w-4" />}
                              {header.key === 'frequency' && <Activity className="h-4 w-4" />}
                              {header.key === 'totalWireConsumed' && <Gauge className="h-4 w-4" />}
                              {header.key === 'arcImpedance' && <Zap className="h-4 w-4" />}
                              {header.label}
                            </div>
                          </div>
                          {minAvgMaxKeys.includes(header.key) && renderHeaderLabels(keyMapping[header.key] || header.key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((row: ZoneViewRow, idx: number) => (
                  <tr
                    key={row.id}
                    className={`h-[52px] ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${idx < filteredData.length - 1 ? 'border-b' : ''}`}
                  >
                    {/* Sticky columns */}
                    {isColumnVisible('sl-no') && (
                      <td className={`sticky z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} px-4 text-center text-gray-600 text-xs font-medium shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ left: getStickyLeft('sl-no') }}>{idx + 1}</td>
                    )}
                    {isColumnVisible('status') && (
                      <td className={`sticky z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} min-w-[60px] w-[60px] max-w-[60px] px-4 text-center shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ width: '60px', left: getStickyLeft('status') }}>
                        <div className="inline-flex justify-center">
                          {renderZoneStatusIcon(row)}
                        </div>
                      </td>
                    )}
                    {isJobNumberColumnShown && (
                      <td className={`sticky z-10 ${idx % 2=== 0 ? 'bg-white' : 'bg-gray-50'} px-4 text-center text-gray-600 text-xs shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ left: getStickyLeft('job-number') }}>{formatJobNumbers(row.jobNumber ?? row.job_number)}</td>
                    )}
                    {isColumnVisible('station') && (
                      <td className={`sticky z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} px-4 text-center text-gray-600 text-xs shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ left: getStickyLeft('station') }}>{row.stationName || '-'}</td>
                    )}
                    {isColumnVisible('actions')&&  !(isCustomerRepresentative && isBuckleArrestorProject) && (
                      <td className={`sticky z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} px-4 text-center`} style={{ left: getStickyLeft('actions'), boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>
                         
                          <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-gray-100"
                            onClick={() => onRowClick?.(row)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                      
                      </td>
                    )}
                    
                    {/* Scrollable columns */}
                    {isColumnVisible('welder-id') && (
                      <td className="px-4 text-center text-gray-600 text-xs">{row.passView?.welderId || '-'}</td>
                    )}
                    {isColumnVisible('bug-type') && (
                      <td className="px-4 text-center text-gray-600 text-xs">{row.bugType}</td>
                    )}
                    {isColumnVisible('torch') && (
                      <td className="px-4 text-center text-gray-600 text-xs">{row.torch}</td>
                    )}
                    {isColumnVisible('pass') && (
                      <td className="px-4 text-center text-gray-600 text-xs">
                      {(() => {
                        let passValue;
                        if (row.isM500System && row.m500ZoneDetails) {
                          // Use the extracted M500 zone details which handles "!" values
                          passValue = row.m500ZoneDetails.PassNum || "";
                        } else {
                          const rawPass = row.pass_name || row.pass || "";
                          if (rawPass) {
                            passValue = rawPass;
                          } else {
                            const rawZone = row.zone_name || row.zone || "";
                            if (rawZone && typeof rawZone === 'string' && rawZone.startsWith('{')) {
                              try {
                                const parsedZone = JSON.parse(rawZone);
                                passValue = `Pass ${parsedZone.PassNum}`;
                              } catch (e) {
                                passValue = rawPass;
                              }
                            } else {
                              passValue = rawPass;
                            }
                          }
                        }
                        return passValue;
                      })()}
                      </td>
                    )}
                    {hasM500Data && isColumnVisible('pipe') && (
                      <td className="px-4 text-center text-gray-600 text-xs">
                        {(() => {
                          let pipeValue;
                          if (row.isM500System && row.m500ZoneDetails) {
                            // Use the extracted M500 zone details which handles "!" values
                            pipeValue = row.m500ZoneDetails.PipeNum || "";
                          } else {
                            const rawPipe = row.pipe_name || "";
                            if (rawPipe) {
                              pipeValue = rawPipe;
                            } else {
                              const rawZone = row.zone_name || row.zone || "";
                              if (rawZone && typeof rawZone === 'string' && rawZone.startsWith('{')) {
                                try {
                                  const parsedZone = JSON.parse(rawZone);
                                  pipeValue = `Pipe ${parsedZone.PipeNum}`;
                                } catch (e) {
                                  pipeValue = "-";
                                }
                              } else {
                                pipeValue = "-";
                              }
                            }
                          }
                          return pipeValue;
                        })()}
                      </td>
                    )}
                    {isColumnVisible('zone') && (
                      <td className="px-4 text-center text-gray-600 text-xs">
                        {(() => {
                          let zoneValue;
                          if (row.isM500System && row.m500ZoneDetails) {
                            // Use the extracted M500 zone details which handles "!" values
                            zoneValue = row.m500ZoneDetails.ZoneNum || "";
                          } else {
                            const rawZone = row.zone_name || row.zone || "";
                            if (rawZone && typeof rawZone === 'string' && rawZone.startsWith('{')) {
                              try {
                                const parsedZone = JSON.parse(rawZone);
                                zoneValue = `Zone ${parsedZone.ZoneNum}`;
                              } catch (e) {
                                zoneValue = rawZone;
                              }
                            } else {
                              zoneValue = rawZone;
                            }
                          }
                          return zoneValue;
                        })()}
                      </td>
                    )}
                    {isColumnVisible('weld-start-time') && (
                      <td className="px-2 text-center text-gray-600 text-xs">{formatTimestampForDisplay(row.weldStartTime)}</td>
                    )}
                    {isColumnVisible('weld-time') && (
                      <td className="px-2 text-center text-gray-600 text-xs">{row.weldTime ? formatDuration(row.weldTime) : '-'}</td>
                    )}
                    {isColumnVisible('auto-increment') && (
                      <td className="px-2 text-center text-gray-600 text-xs border-r">{row.autoincrement || '-'}</td>
                    )}
                     
                     {/* Parameter columns */}
                         {dynamicHeaders.map((header, index) => (
                           <td key={header.key} className="px-2 py-2 h-[52px] text-xs min-w-[150px] max-w-[250px]">
                             {header.render ? header.render(row) : row[header.key as keyof ZoneViewRow]}
                           </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
          </div>
        )}
      </div>
    </div>
  );
});

ZoneViewTable.displayName = 'ZoneViewTable';

export default ZoneViewTable; 